@if (!resetSuccess()) {
  <div class="reset-password-page screen-bottom-halo h-100">
    <div class="logo-container">
      <img class="logo-icon" src="assets/images/explain-logo-900.svg" alt="explain_logo" />
      <img class="logo-text" src="assets/images/explain-text-900.svg" alt="explain_text" />
    </div>

    <div class="reset-password-card">
      <form class="card-content" (ngSubmit)="onSubmit()">
        <div class="card-header">
          <div class="key-icon-wrapper">
            <i class="ph ph-key key-icon"></i>
          </div>
          <h1 class="card-title">{{ 'new-password.title' | translate }}</h1>
          <p class="card-subtitle">{{ 'new-password.sub-title' | translate }}</p>
        </div>

        <!-- Password field -->
        <div class="password-field">
          <label class="field-label">{{ 'new-password.enter-new-password' | translate | uppercase }}</label>
          <div class="input-wrapper">
            <input
              [type]="showPassword() ? 'text' : 'password'"
              [value]="password()"
              (input)="onPasswordInput($any($event.target).value)"
              class="input-password"
              [ngClass]="{ 'input-password--error': hasInteracted() && !allMet() && password().length > 0 }"
              [placeholder]="'new-password.placeholder' | translate"
              autocomplete="new-password"
              data-cy="reset-password-password-input"
            />
            <button
              type="button"
              class="toggle-password"
              (click)="toggleShowPassword()"
              [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'"
            >
              @if (showPassword()) {
                <img class="toggle-icon" src="assets/images/hide-password-icon.svg" alt="" />
              } @else {
                <img class="toggle-icon" src="assets/images/show-password-icon.svg" alt="" />
              }
            </button>
          </div>

          @if (hasInteracted() && password().length > 0) {
            <div class="strength-bar">
              <span class="strength-label">{{ 'create-password.strength-label' | translate }}</span>
              <span class="strength-value" [style.color]="strength().color">
                {{ strength().labelKey ? (strength().labelKey | translate) : '' }}
              </span>
            </div>
            <div class="strength-segments">
              @for (i of [1, 2, 3]; track i) {
                <div class="segment" [style.background-color]="i <= strength().level ? strength().color : 'var(--basic-300)'"></div>
              }
            </div>
          }

          @if (hasInteracted()) {
            <div class="requirements-list">
              <div class="requirement-item" [class.requirement-item--met]="requirements()[0].met">
                @if (requirements()[0].met) {
                  <img class="requirement-icon" src="assets/images/icon-green-checkmark-circle.svg" alt="" />
                } @else {
                  <img class="requirement-icon" src="assets/images/icon-radio-button-off.svg" alt="" />
                }
                <span>{{ requirements()[0].labelKey | translate }}</span>
              </div>
              <div class="requirements-grid">
                @for (r of requirements().slice(1, 3); track r.id) {
                  <div class="requirement-item" [class.requirement-item--met]="r.met">
                    @if (r.met) {
                      <img class="requirement-icon" src="assets/images/icon-green-checkmark-circle.svg" alt="" />
                    } @else {
                      <img class="requirement-icon" src="assets/images/icon-radio-button-off.svg" alt="" />
                    }
                    <span>{{ r.labelKey | translate }}</span>
                  </div>
                }
              </div>
              <div class="requirements-grid">
                @for (r of requirements().slice(3, 5); track r.id) {
                  <div class="requirement-item" [class.requirement-item--met]="r.met">
                    @if (r.met) {
                      <img class="requirement-icon" src="assets/images/icon-green-checkmark-circle.svg" alt="" />
                    } @else {
                      <img class="requirement-icon" src="assets/images/icon-radio-button-off.svg" alt="" />
                    }
                    <span>{{ r.labelKey | translate }}</span>
                  </div>
                }
              </div>
            </div>
          }
        </div>

        <!-- Confirm password field -->
        <div class="password-field">
          <label class="field-label">{{ 'new-password.confirm-new-password' | translate | uppercase }}</label>
          <div class="input-wrapper">
            <input
              [type]="showConfirmPassword() ? 'text' : 'password'"
              [value]="confirmPassword()"
              (input)="onConfirmPasswordInput($any($event.target).value)"
              (blur)="onConfirmBlur()"
              class="input-password"
              [ngClass]="{ 'input-password--error': confirmMismatch() }"
              autocomplete="new-password"
              data-cy="reset-password-confirm-input"
            />
            <button
              type="button"
              class="toggle-password"
              (click)="toggleShowConfirmPassword()"
              [attr.aria-label]="showConfirmPassword() ? 'Hide password' : 'Show password'"
            >
              @if (showConfirmPassword()) {
                <img class="toggle-icon" src="assets/images/hide-password-icon.svg" alt="" />
              } @else {
                <img class="toggle-icon" src="assets/images/show-password-icon.svg" alt="" />
              }
            </button>
          </div>
          @if (confirmMismatch()) {
            <div class="error-box">
              <i class="ph ph-warning-circle error-icon"></i>
              <span class="error-text">{{ 'update-password.invalid-confirmation-password' | translate }}</span>
            </div>
          }
        </div>

        <!-- Submit + back link -->
        <div class="submit-section">
          <button
            type="submit"
            class="btn-primary"
            [class.btn-primary--disabled]="!canSubmit()"
            [disabled]="!canSubmit()"
            data-cy="reset-password-submit"
          >
            @if (isSubmitting()) {
              <svg
                class="btn-loader"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="12" y1="2" x2="12" y2="6"></line>
                <line x1="12" y1="18" x2="12" y2="22"></line>
                <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                <line x1="2" y1="12" x2="6" y2="12"></line>
                <line x1="18" y1="12" x2="22" y2="12"></line>
                <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
              </svg>
            }
            <span>{{ isSubmitting() ? ('new-password.submitting' | translate) : ('new-password.reset' | translate) }}</span>
          </button>
          <a [routerLink]="['/login']" class="back-link" data-cy="reset-password-back-link">
            <svg
              class="back-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            <span>{{ 'new-password.back-to-login' | translate }}</span>
          </a>
        </div>
      </form>
    </div>
  </div>
} @else {
  <app-reset-password-success></app-reset-password-success>
}
